import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const PERIOD_TYPES = ["daily", "weekly", "season"];

const EVENT_TYPES = [
  "game_played",
  "score_reached",
  "ad_reward_watched",
  "consumable_used",
];

const PROGRESS_MODES = ["increment", "accumulate", "max"];

const DIFFICULTIES = ["easy", "medium", "hard"];

const CATEGORIES = ["play", "score", "ads", "consumable"];

const DEFAULT_FORM = {
  missionId: "",
  periodType: "daily",

  titleKey: "",
  descriptionKey: "",

  eventType: "game_played",
  progressMode: "increment",

  minigameCode: "any",
  targetId: "",

  targetAmount: 1,

  rewardCoins: 10,
  rewardTokens: 0,
  rewardFreeze: 0,
  rewardLifeGuard: 0,

  difficulty: "easy",
  category: "play",
  weight: 10,

  active: true,
};

const EVENT_TYPE_LABELS = {
  game_played: "Jugar partidas",
  score_reached: "Alcanzar puntaje",
  ad_reward_watched: "Ver anuncio de misión",
  consumable_used: "Usar consumible",
};

const PROGRESS_MODE_LABELS = {
  increment: "Incrementar",
  accumulate: "Acumular",
  max: "Máximo alcanzado",
};

const PERIOD_TYPE_LABELS = {
  daily: "Diaria",
  weekly: "Semanal",
  season: "Temporada",
};

const DIFFICULTY_LABELS = {
  easy: "Fácil",
  medium: "Media",
  hard: "Difícil",
};

const CATEGORY_LABELS = {
  play: "Jugar",
  score: "Puntaje",
  ads: "Anuncios",
  consumable: "Consumibles",
};

function normalizeMissionId(value) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
}

function buildDefaultKeys() {
  return {
    titleKey: "",
    descriptionKey: "",
  };
}

function getRecommendedProgressMode(eventType) {
  if (eventType === "score_reached") return "max";
  return "increment";
}

function getRecommendedTargetId(eventType) {
  if (eventType === "ad_reward_watched") return "mission_ad";
  if (eventType === "consumable_used") return "freeze";
  return "";
}

function formatDate(value) {
  if (!value) return "—";

  const date = value.toDate ? value.toDate() : new Date(value);

  return date.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function MissionSwitch({ checked, onChange, disabled }) {
  return (
    <label className="switch-row">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="switch-ui"></span>
      <strong>{checked ? "Activa" : "Inactiva"}</strong>
    </label>
  );
}

function FieldHint({ children }) {
  return <small className="field-hint">{children}</small>;
}

function MissionsPage() {
  const [missions, setMissions] = useState([]);
  const [minigames, setMinigames] = useState([]);

  const [dailyMissionIds, setDailyMissionIds] = useState([]);
  const [dailyPeriodId, setDailyPeriodId] = useState("");
  const [dailyMissionsUpdatedAt, setDailyMissionsUpdatedAt] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingMinigames, setLoadingMinigames] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const [editingMission, setEditingMission] = useState(null);
  const [editForm, setEditForm] = useState(DEFAULT_FORM);

  const activeDailyMissions = useMemo(() => {
    return dailyMissionIds
      .map((missionId) =>
        missions.find(
          (mission) =>
            mission.id === missionId || mission.missionId === missionId
        )
      )
      .filter(Boolean);
  }, [dailyMissionIds, missions]);

  const filteredMissions = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return missions.filter((mission) => {
      const missionId = String(
        mission.missionId || mission.id || ""
      ).toLowerCase();
      const titleKey = String(mission.titleKey || "").toLowerCase();
      const eventType = String(mission.eventType || "").toLowerCase();
      const category = String(mission.category || "").toLowerCase();

      return (
        missionId.includes(normalizedSearch) ||
        titleKey.includes(normalizedSearch) ||
        eventType.includes(normalizedSearch) ||
        category.includes(normalizedSearch)
      );
    });
  }, [missions, searchText]);

  async function loadGameConfig() {
    const configSnapshot = await getDoc(doc(db, "config", "game"));

    if (!configSnapshot.exists()) {
      setDailyMissionIds([]);
      setDailyPeriodId("");
      setDailyMissionsUpdatedAt(null);
      return;
    }

    const data = configSnapshot.data();

    setDailyMissionIds(Array.isArray(data.dailyMissionIds) ? data.dailyMissionIds : []);
    setDailyPeriodId(data.dailyPeriodId || "");
    setDailyMissionsUpdatedAt(data.dailyMissionsUpdatedAt || null);
  }

  async function loadMissions() {
    const missionsQuery = query(
      collection(db, "config", "game", "missions"),
      orderBy("weight", "desc")
    );

    const snapshot = await getDocs(missionsQuery);

    const data = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    setMissions(data);
  }

  async function loadMinigames() {
    try {
      setLoadingMinigames(true);

      const minigamesQuery = query(
        collection(db, "minigames"),
        orderBy("title", "asc")
      );

      const snapshot = await getDocs(minigamesQuery);

      const data = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }));

      setMinigames(data);
    } catch (error) {
      console.error("Error cargando minijuegos:", error);
      alert("No se pudieron cargar los minijuegos para el desplegable.");
    } finally {
      setLoadingMinigames(false);
    }
  }

  async function loadPageData() {
    try {
      setLoading(true);

      await Promise.all([loadMissions(), loadMinigames(), loadGameConfig()]);
    } catch (error) {
      console.error("Error cargando misiones:", error);
      alert("No se pudieron cargar las misiones.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPageData();
  }, []);

  function openCreateModal() {
    setForm(DEFAULT_FORM);
    setShowCreateModal(true);
  }

  function closeCreateModal() {
    if (saving) return;

    setShowCreateModal(false);
    setForm(DEFAULT_FORM);
  }

  function updateForm(field, value) {
    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "missionId") {
        const normalizedId = normalizeMissionId(value);
        //const keys = buildDefaultKeys(normalizedId);

        next.missionId = normalizedId;
        //next.titleKey = keys.titleKey;
        //next.descriptionKey = keys.descriptionKey;
      }

      if (field === "eventType") {
        next.progressMode = getRecommendedProgressMode(value);
        next.targetId = getRecommendedTargetId(value);

        if (value === "score_reached") {
          next.category = "score";
        }

        if (value === "ad_reward_watched") {
          next.category = "ads";
          next.minigameCode = "any";
          next.targetAmount = 1;
        }

        if (value === "consumable_used") {
          next.category = "consumable";
          next.minigameCode = "any";
          next.targetAmount = 1;
        }

        if (value === "game_played") {
          next.category = "play";
          next.minigameCode = "any";
        }
      }

      return next;
    });
  }

  function updateEditForm(field, value) {
    setEditForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "eventType") {
        next.progressMode = getRecommendedProgressMode(value);
        next.targetId = getRecommendedTargetId(value);

        if (value === "score_reached") {
          next.category = "score";
        }

        if (value === "ad_reward_watched") {
          next.category = "ads";
          next.minigameCode = "any";
          next.targetAmount = 1;
        }

        if (value === "consumable_used") {
          next.category = "consumable";
          next.minigameCode = "any";
          next.targetAmount = 1;
        }

        if (value === "game_played") {
          next.category = "play";
        }
      }

      return next;
    });
  }

  function normalizeMissionData(data, forcedMissionId = null) {
    const missionId = forcedMissionId || normalizeMissionId(data.missionId);

    return {
      missionId,
      periodType: data.periodType || "daily",

      titleKey: data.titleKey.trim(),
      descriptionKey: data.descriptionKey.trim(),

      eventType: data.eventType,
      progressMode: data.progressMode,

      minigameCode: data.minigameCode.trim() || "any",
      targetId: data.targetId.trim(),

      targetAmount: Math.max(1, Number(data.targetAmount) || 1),

      rewardCoins: Math.max(0, Number(data.rewardCoins) || 0),
      rewardTokens: Math.max(0, Number(data.rewardTokens) || 0),
      rewardFreeze: Math.max(0, Number(data.rewardFreeze) || 0),
      rewardLifeGuard: Math.max(0, Number(data.rewardLifeGuard) || 0),

      difficulty: data.difficulty || "easy",
      category: data.category || "play",
      weight: Math.max(1, Number(data.weight) || 1),

      active: Boolean(data.active),
    };
  }

  function validateMissionData(data) {
    if (!data.missionId) {
      alert("Ingresa un ID válido para la misión.");
      return false;
    }

    /*if (!data.titleKey) {
      alert("Ingresa la key del título.");
      return false;
    }

    if (!data.descriptionKey) {
      alert("Ingresa la key de la descripción.");
      return false;
    }*/

    if (
      data.eventType === "ad_reward_watched" &&
      data.targetId !== "mission_ad"
    ) {
      alert('Para anuncios de misión, el Target ID debe ser "mission_ad".');
      return false;
    }

    if (
      data.eventType === "consumable_used" &&
      data.targetId !== "freeze" &&
      data.targetId !== "lifeguard"
    ) {
      alert('Para consumibles, el Target ID debe ser "freeze" o "lifeguard".');
      return false;
    }

    return true;
  }

  async function handleCreateMission(e) {
    e.preventDefault();

    const missionData = normalizeMissionData(form);

    if (!validateMissionData(missionData)) return;

    try {
      setSaving(true);

      await setDoc(
        doc(db, "config", "game", "missions", missionData.missionId),
        missionData
      );

      setMissions((prev) => {
        const withoutExisting = prev.filter(
          (mission) => mission.id !== missionData.missionId
        );

        return [
          {
            id: missionData.missionId,
            ...missionData,
          },
          ...withoutExisting,
        ];
      });

      closeCreateModal();
      alert("Misión creada correctamente.");
    } catch (error) {
      console.error("Error creando misión:", error);
      alert("No se pudo crear la misión.");
    } finally {
      setSaving(false);
    }
  }

  function openEditModal(mission) {
    const missionData = {
      ...DEFAULT_FORM,
      ...mission,
      missionId: mission.missionId || mission.id,
    };

    setEditingMission(mission);
    setEditForm(missionData);
  }

  function closeEditModal() {
    if (saving) return;

    setEditingMission(null);
    setEditForm(DEFAULT_FORM);
  }

  async function handleUpdateMission(e) {
    e.preventDefault();

    if (!editingMission) return;

    const missionId = editingMission.id;
    const missionData = normalizeMissionData(editForm, missionId);

    if (!validateMissionData(missionData)) return;

    try {
      setSaving(true);

      await updateDoc(
        doc(db, "config", "game", "missions", missionId),
        missionData
      );

      setMissions((prev) =>
        prev.map((mission) =>
          mission.id === missionId
            ? {
              id: missionId,
              ...missionData,
            }
            : mission
        )
      );

      closeEditModal();
      alert("Misión actualizada correctamente.");
    } catch (error) {
      console.error("Error actualizando misión:", error);
      alert("No se pudo actualizar la misión.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleDailyMission(mission) {
    const missionId = mission.missionId || mission.id;

    const isAlreadyDaily = dailyMissionIds.includes(missionId);

    const nextDailyMissionIds = isAlreadyDaily
      ? dailyMissionIds.filter((id) => id !== missionId)
      : [...dailyMissionIds, missionId];

    try {
      setSaving(true);

      await updateDoc(doc(db, "config", "game"), {
        dailyMissionIds: nextDailyMissionIds,
        dailyMissionsUpdatedAt: serverTimestamp(),
      });

      setDailyMissionIds(nextDailyMissionIds);
      setDailyMissionsUpdatedAt(new Date());
    } catch (error) {
      console.error("Error actualizando misiones diarias:", error);
      alert("No se pudieron actualizar las misiones diarias.");
    } finally {
      setSaving(false);
    }
  }

  function renderMinigameSelect(value, onChange, disabled) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loadingMinigames}
      >
        <option value="any">Cualquier minijuego</option>

        {minigames.map((minigame) => (
          <option key={minigame.id} value={minigame.type || minigame.id}>
            {minigame.title || minigame.id} ({minigame.type || minigame.id})
          </option>
        ))}
      </select>
    );
  }

  function renderMissionFormContent(data, updateFn, isEditing = false) {
    return (
      <>
        <div className="form-grid">
          <label>
            ID de misión
            <input
              type="text"
              placeholder="daily_play_3_games"
              value={data.missionId}
              onChange={(e) => updateFn("missionId", e.target.value)}
              disabled={saving || isEditing}
            />
            <FieldHint>
              {isEditing
                ? "No se puede cambiar porque es el ID del documento."
                : "Identificador único. También será el ID del documento."}
            </FieldHint>
          </label>

          <label>
            Periodo
            <select
              value={data.periodType}
              onChange={(e) => updateFn("periodType", e.target.value)}
              disabled={saving}
            >
              {PERIOD_TYPES.map((item) => (
                <option key={item} value={item}>
                  {PERIOD_TYPE_LABELS[item] || item}
                </option>
              ))}
            </select>
            <FieldHint>Define si la misión es diaria, semanal o de temporada.</FieldHint>
          </label>

          <label>
            Key del título
            <div className="inline-field-actions">
              <input
                type="text"
                value={data.titleKey}
                onChange={(e) => updateFn("titleKey", e.target.value)}
                disabled={saving}
                placeholder="Vacío = texto automático"
              />

              <button
                type="button"
                className="small-button"
                onClick={() => updateFn("titleKey", "")}
                disabled={saving || !data.titleKey}
              >
                Vaciar
              </button>
            </div>

            <FieldHint>
              Opcional. Si está vacío, el juego genera el título automáticamente.
            </FieldHint>
          </label>

          <label>
            Key de descripción
            <div className="inline-field-actions">
              <input
                type="text"
                value={data.descriptionKey}
                onChange={(e) => updateFn("descriptionKey", e.target.value)}
                disabled={saving}
                placeholder="Vacío = texto automático"
              />

              <button
                type="button"
                className="small-button"
                onClick={() => updateFn("descriptionKey", "")}
                disabled={saving || !data.descriptionKey}
              >
                Vaciar
              </button>
            </div>

            <FieldHint>
              Opcional. Si está vacía, el juego genera la descripción automáticamente.
            </FieldHint>
          </label>

          <label>
            Tipo de evento
            <select
              value={data.eventType}
              onChange={(e) => updateFn("eventType", e.target.value)}
              disabled={saving}
            >
              {EVENT_TYPES.map((item) => (
                <option key={item} value={item}>
                  {EVENT_TYPE_LABELS[item] || item}
                </option>
              ))}
            </select>
            <FieldHint>Acción que debe ocurrir para avanzar la misión.</FieldHint>
          </label>

          <label>
            Modo de progreso
            <select
              value={data.progressMode}
              onChange={(e) => updateFn("progressMode", e.target.value)}
              disabled={saving}
            >
              {PROGRESS_MODES.map((item) => (
                <option key={item} value={item}>
                  {PROGRESS_MODE_LABELS[item] || item}
                </option>
              ))}
            </select>
            <FieldHint>Cómo se calcula el progreso del jugador.</FieldHint>
          </label>

          <label>
            Minijuego
            {renderMinigameSelect(
              data.minigameCode,
              (value) => updateFn("minigameCode", value),
              saving
            )}
            <FieldHint>Selecciona un minijuego o cualquier minijuego.</FieldHint>
          </label>

          <label>
            Objetivo específico
            <input
              type="text"
              placeholder="mission_ad, freeze, lifeguard o vacío"
              value={data.targetId}
              onChange={(e) => updateFn("targetId", e.target.value)}
              disabled={saving}
            />
            <FieldHint>Usado para mission_ad, freeze o lifeguard.</FieldHint>
          </label>

          <label>
            Cantidad objetivo
            <input
              type="number"
              min="1"
              value={data.targetAmount}
              onChange={(e) => updateFn("targetAmount", e.target.value)}
              disabled={saving}
            />
            <FieldHint>Número necesario para completar la misión.</FieldHint>
          </label>

          <label>
            Dificultad
            <select
              value={data.difficulty}
              onChange={(e) => updateFn("difficulty", e.target.value)}
              disabled={saving}
            >
              {DIFFICULTIES.map((item) => (
                <option key={item} value={item}>
                  {DIFFICULTY_LABELS[item] || item}
                </option>
              ))}
            </select>
            <FieldHint>Clasificación interna de dificultad.</FieldHint>
          </label>

          <label>
            Categoría
            <select
              value={data.category}
              onChange={(e) => updateFn("category", e.target.value)}
              disabled={saving}
            >
              {CATEGORIES.map((item) => (
                <option key={item} value={item}>
                  {CATEGORY_LABELS[item] || item}
                </option>
              ))}
            </select>
            <FieldHint>Grupo al que pertenece la misión.</FieldHint>
          </label>

          <label>
            Peso de aparición
            <input
              type="number"
              min="1"
              value={data.weight}
              onChange={(e) => updateFn("weight", e.target.value)}
              disabled={saving}
            />
            <FieldHint>Más alto significa más probable.</FieldHint>
          </label>
        </div>

        <div className="rewards-box">
          <h4>Recompensas</h4>

          <div className="reward-grid">
            <label>
              Monedas
              <input
                type="number"
                min="0"
                value={data.rewardCoins}
                onChange={(e) => updateFn("rewardCoins", e.target.value)}
                disabled={saving}
              />
            </label>

            <label>
              Tokens
              <input
                type="number"
                min="0"
                value={data.rewardTokens}
                onChange={(e) => updateFn("rewardTokens", e.target.value)}
                disabled={saving}
              />
            </label>

            <label>
              Freeze
              <input
                type="number"
                min="0"
                value={data.rewardFreeze}
                onChange={(e) => updateFn("rewardFreeze", e.target.value)}
                disabled={saving}
              />
            </label>

            <label>
              LifeGuard
              <input
                type="number"
                min="0"
                value={data.rewardLifeGuard}
                onChange={(e) => updateFn("rewardLifeGuard", e.target.value)}
                disabled={saving}
              />
            </label>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <section className="admin-section-card missions-page">
        <div className="section-header missions-header">
          <div>
            <span className="section-eyebrow">Objetivos</span>
            <h2>Misiones</h2>
            <p>Crea y administra misiones en config/game/missions.</p>
          </div>

          <div className="section-actions">
            <button
              className="primary-action-button"
              type="button"
              onClick={openCreateModal}
              disabled={saving}
            >
              ＋ Crear misión
            </button>

            <button
              className="secondary-button"
              type="button"
              onClick={loadPageData}
              disabled={saving}
            >
              🔄 Actualizar
            </button>

            <span className="section-count">
              {filteredMissions.length} / {missions.length} misiones
            </span>
          </div>
        </div>

        <div className="missions-active-card">
          <div>
            <span className="section-eyebrow">Misiones diarias activas</span>
            {/*<h3>{dailyPeriodId || "Periodo diario no configurado"}</h3>*/}
            <p>
              Última actualización: {formatDate(dailyMissionsUpdatedAt)}
            </p>
          </div>

          <span className="section-count">
            {dailyMissionIds.length} activas
          </span>
        </div>

        <div className="active-missions-grid">
          {dailyMissionIds.length === 0 ? (
            <p className="empty-state">
              No hay misiones activas en dailyMissionIds.
            </p>
          ) : (
            dailyMissionIds.map((missionId) => {
              const mission = missions.find(
                (item) => item.id === missionId || item.missionId === missionId
              );

              return (
                <article
                  className={`active-mission-card ${mission ? "" : "active-mission-card--missing"
                    }`}
                  key={missionId}
                >
                  <div className="active-mission-card__icon">🎯</div>

                  <div>
                    <strong>{mission?.missionId || missionId}</strong>

                    <span>
                      {mission
                        ? EVENT_TYPE_LABELS[mission.eventType] ||
                        mission.eventType
                        : "No existe en config/game/missions"}
                    </span>

                    {mission && (
                      <small>
                        Objetivo: {mission.targetAmount} ·{" "}
                        {CATEGORY_LABELS[mission.category] || mission.category}
                      </small>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>

        <div className="users-toolbar">
          <div className="search-box users-search-box">
            <span>🔎</span>

            <input
              type="text"
              placeholder="Buscar por ID, key, evento o categoría..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {searchText && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setSearchText("")}
            >
              Limpiar
            </button>
          )}
        </div>

        {loading ? (
          <main className="users-loading-state">
            <section className="loading-card">
              <div className="banter-loader" aria-hidden="true">
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
              </div>

              <h2>Cargando dashboard...</h2>
              <p>Consultando usuarios, minijuegos, tienda, misiones y temporada.</p>
            </section>
          </main>
        ) : (
          <div className="table-wrapper">
            <table className="admin-table missions-table">
              <thead>
                <tr>
                  <th>Misión</th>
                  <th>Evento</th>
                  <th>Objetivo</th>
                  <th>Recompensas</th>
                  <th>Peso</th>
                  <th>Daily</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {filteredMissions.map((mission) => {
                  const missionId = mission.missionId || mission.id;
                  const isDailySelected = dailyMissionIds.includes(missionId);

                  return (
                    <tr key={mission.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-name-row">
                            <span className="user-avatar">🎯</span>

                            <div>
                              <strong>{missionId}</strong>
                              <span>
                                {mission.titleKey
                                  ? mission.titleKey
                                  : "Texto automático"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="mission-stack">
                          <strong>
                            {EVENT_TYPE_LABELS[mission.eventType] ||
                              mission.eventType}
                          </strong>
                          <span>
                            {PROGRESS_MODE_LABELS[mission.progressMode] ||
                              mission.progressMode}
                          </span>
                          <span>
                            {CATEGORY_LABELS[mission.category] ||
                              mission.category}{" "}
                            /{" "}
                            {DIFFICULTY_LABELS[mission.difficulty] ||
                              mission.difficulty}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="mission-stack">
                          <strong>{mission.targetAmount}</strong>
                          <span>minijuego: {mission.minigameCode || "any"}</span>
                          <span>objetivo: {mission.targetId || "—"}</span>
                        </div>
                      </td>

                      <td>
                        <ul className="resource-list">
                          <li>
                            <span className="resource-emoji">💰</span>
                            <strong>{mission.rewardCoins ?? 0}</strong>
                          </li>
                          <li>
                            <span className="resource-emoji">🎟️</span>
                            <strong>{mission.rewardTokens ?? 0}</strong>
                          </li>
                          <li>
                            <span className="resource-emoji">❄️</span>
                            <strong>{mission.rewardFreeze ?? 0}</strong>
                          </li>
                          <li>
                            <span className="resource-emoji">🛡️</span>
                            <strong>{mission.rewardLifeGuard ?? 0}</strong>
                          </li>
                        </ul>
                      </td>

                      <td>{mission.weight ?? 1}</td>

                      <td>
                        <button
                          type="button"
                          className={
                            isDailySelected
                              ? "status-badge status-badge--enabled status-badge--button"
                              : "status-badge status-badge--disabled status-badge--button"
                          }
                          onClick={() => toggleDailyMission(mission)}
                          disabled={saving || mission.periodType !== "daily"}
                          title={
                            mission.periodType !== "daily"
                              ? "Solo las misiones de periodo daily pueden activarse como diarias"
                              : isDailySelected
                                ? "Quitar de misiones diarias"
                                : "Agregar a misiones diarias"
                          }
                        >
                          {isDailySelected ? "Activa hoy" : "No diaria"}
                        </button>
                      </td>

                      <td>
                        <span
                          className={
                            mission.active
                              ? "status-badge status-badge--enabled"
                              : "status-badge status-badge--disabled"
                          }
                        >
                          {mission.active ? "Activa" : "Inactiva"}
                        </span>
                      </td>

                      <td>
                        <button
                          className="small-button small-button--dark"
                          onClick={() => openEditModal(mission)}
                          disabled={saving}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredMissions.length === 0 && (
              <p className="empty-state">No hay misiones para mostrar.</p>
            )}
          </div>
        )}
      </section>

      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="edit-modal edit-modal--wide">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Nueva misión</span>
                <h3>Crear misión</h3>
                <p>El ID del documento será igual al ID de misión.</p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeCreateModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateMission} className="edit-form">
              {renderMissionFormContent(form, updateForm, false)}

              <div className="edit-modal__actions">
                <MissionSwitch
                  checked={form.active}
                  onChange={(value) => updateForm("active", value)}
                  disabled={saving}
                />

                <div className="edit-modal__actions-right">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={closeCreateModal}
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="primary-action-button"
                    disabled={saving}
                  >
                    {saving ? "Creando..." : "Crear misión"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingMission && (
        <div className="modal-backdrop">
          <div className="edit-modal edit-modal--wide">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Editar</span>
                <h3>Editar misión</h3>
                <p>{editingMission.id}</p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeEditModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateMission} className="edit-form">
              {renderMissionFormContent(editForm, updateEditForm, true)}

              <div className="edit-modal__actions">
                <MissionSwitch
                  checked={editForm.active}
                  onChange={(value) => updateEditForm("active", value)}
                  disabled={saving}
                />

                <div className="edit-modal__actions-right">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={closeEditModal}
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="primary-action-button"
                    disabled={saving}
                  >
                    {saving ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default MissionsPage;